import {
    Button,
    Grid,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Stack,
    Text,
} from "@chakra-ui/react"
import Big from "big.js"
import { Global } from "container/global"
import { Vesting, VestingType } from "container/reward/Vesting"
import { User } from "container/user"
import { loadTree } from "lib/merkleTree"
import { TxButton } from "page/Home/component/TxButton"
import React, { useCallback, useState } from "react"
import { useAsync } from "react-use"
import { soliditySha3, toWei } from "web3-utils"

import { SelectableItem } from "./SelectableItem"
import { SelectAll } from "./SelectAll"
import { Claim, ClaimableRewardsItem, Selected } from "./type"
import { useSelectable } from "./useSelectable"
import { formatNumber } from "./util"

function ClaimModal() {
    const [type, setType] = useState<VestingType>()

    const {
        state: { address },
    } = User.useContainer()

    const {
        state: {
            modal: { isClaimOpen },
        },
        actions: { toggleClaimModal },
    } = Global.useContainer()

    const {
        state,
        actions: { claimWeeks },
    } = Vesting.useContainer()

    const { value: items = [], loading: isLoading } = useAsync(async () => {
        if (!type || !address) {
            return
        }
        const claimStatus = state.claimStatus[type]
        const snapshot = state.snapshot[type]
        const report = state.report[type]
        if (claimStatus && snapshot && report) {
            const claimableRewards = await getClaimableRewards(address, claimStatus, snapshot, report)
            return claimableRewards
        }
    }, [address, state.claimStatus, state.report, state.snapshot, type])

    const { selected, onSelectChange, onSelectAll, isSelectedAll, selectedAmount, selectedLength } = useSelectable(
        items,
    )

    const handleClaim = useCallback(async () => {
        const claims = getClaimsBySelected(selected)
        if (address && type && claims) {
            await claimWeeks(address, type, claims)
            toggleClaimModal()
            setType(undefined)
        }
    }, [address, claimWeeks, selected, toggleClaimModal, type])

    const handleClose = useCallback(() => {
        toggleClaimModal()
        setType(undefined)
    }, [toggleClaimModal])

    return (
        <Modal isCentered={true} size="sm" isOpen={isClaimOpen} onClose={handleClose} scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent bg="gray.800" color="gray.200">
                <ModalHeader fontWeight="400" fontSize="sm">
                    Claim Rewards
                </ModalHeader>
                <ModalCloseButton />

                <ModalBody pb="1.5rem">
                    <Text p={4} fontSize="sm">
                        To avoid ETH gas fee limitation, we've separate vesting and non-vesting claiming. You should be
                        able to claim available rewards from both pools.'
                    </Text>
                    {(!type || isLoading) && (
                        <Stack spacing={2}>
                            <Button
                                isLoading={isLoading}
                                fontWeight="400"
                                fontSize="md"
                                justifyContent="left"
                                bg="gray.900"
                                onClick={() => setType(VestingType.immediately)}
                                _hover={{ bg: "rgba(255, 255, 255, 0.16)" }}
                            >
                                Claim from Non-vesting Pool
                            </Button>
                            <Button
                                isLoading={isLoading}
                                fontWeight="400"
                                fontSize="md"
                                justifyContent="left"
                                bg="gray.900"
                                onClick={() => setType(VestingType.timed)}
                                _hover={{ bg: "rgba(255, 255, 255, 0.16)" }}
                            >
                                Claim from Vesting Pool
                            </Button>
                        </Stack>
                    )}
                    {type &&
                        !isLoading &&
                        items.map((item, index) => (
                            <SelectableItem
                                key={index}
                                index={index}
                                data={item}
                                selected={selected}
                                onChange={onSelectChange}
                                isLast={index === items.length - 1}
                                selectedLength={selectedLength}
                            />
                        ))}
                </ModalBody>
                {type && !isLoading && (
                    <ModalFooter>
                        <Grid w="100%" gap="16px">
                            <SelectAll isChecked={isSelectedAll} onChange={onSelectAll} />
                            <TxButton
                                colorScheme={"perpetual"}
                                onClick={handleClaim}
                                disabled={selectedAmount.toNumber() ? false : true}
                            >
                                Claim {formatNumber(selectedAmount)} PERP
                            </TxButton>
                        </Grid>
                    </ModalFooter>
                )}
            </ModalContent>
        </Modal>
    )
}

export default ClaimModal

function getClaimableRewards(
    address: string,
    claimStatus: any,
    snapshot: any,
    report: any,
): Promise<ClaimableRewardsItem[]> {
    return new Promise(resolve => {
        setTimeout(() => {
            const claimableRewards: ClaimableRewardsItem[] = []
            const now = Math.round(new Date().getTime() / 1000)
            claimStatus.forEach((status: boolean, index: number) => {
                const snapshotWeekly = snapshot[index + 1]
                const balances = report[index]
                const balance = balances && balances[address]
                if (
                    !status &&
                    snapshotWeekly &&
                    now >= snapshotWeekly.timestamp &&
                    balances &&
                    balance &&
                    balance !== "0"
                ) {
                    const merkleTree = loadTree(balances)
                    const proof = merkleTree.getHexProof(soliditySha3(address, toWei(balance)))
                    claimableRewards.push({
                        week: snapshotWeekly.weekNumber,
                        balance: Big(balance),
                        merkleProof: proof,
                        weekEndTimeIso: snapshotWeekly.timestamp,
                        weekStartTimeIso: snapshotWeekly.timestamp,
                    })
                }
            })
            resolve(claimableRewards)
        })
    })
}

function getClaimsBySelected(selected: Selected): Claim[] {
    return Object.values(selected).map((item: ClaimableRewardsItem) => {
        return {
            week: item.week,
            balance: toWei(item.balance.toString()),
            merkleProof: item.merkleProof,
        }
    })
}
